import { GoogleAuthProvider, User, createUserWithEmailAndPassword, getAuth, onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup } from '@angular/fire/auth';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { sleep } from '@/utils/helpers';
import { FirebaseApp } from '@angular/fire/app';
import { AuthProvider, getAuth as initializeAuth, OAuthProvider, UserCredential } from 'firebase/auth';
import { allowedDomains } from './blockedDomains';
import { environment } from 'src/environments/environment';

const provider = new GoogleAuthProvider();

@Injectable({
  providedIn: 'root'
})


export class AppService {

  public user?: User | null = null;
  auth: any;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    public firebaseApp: FirebaseApp
  ) {

    // this.auth = getAuth();
    this.auth = initializeAuth(firebaseApp); // initialize firebase auth

    onAuthStateChanged(
      this.auth,
      (user) => {
        if (user) {
          this.user = user;
        } else {
          this.user = undefined;
        }
      },
      (e) => {
        if (environment.debug)
          console.log(e)
        this.user = undefined;
      }
    );
  }

  async registerWithEmail(email: string, password: string) {
    try {
      const result = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password
      );
      this.user = result.user;
      this.router.navigate(['/dashboard']);
      return result;
    } catch (error: any) {
      this.toastr.error(error.message);
    }
  }

  async loginWithEmail(email: string, password: string) {
    try {
      const result = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      );
      this.user = result.user;
      this.router.navigate(['/dashboard']);

      return result;
    } catch (error: any) {
      this.toastr.error(error.message);
    }
  }

  async signInByGoogle() {
    try {
      const result = await signInWithPopup(this.auth, provider);
      this.user = result.user;
      this.router.navigate(['/dashboard']);

      return result;
    } catch (error: any) {
      this.toastr.error(error.message);
    }
  }

  async getProfile() {
    try {
      await sleep(500);
      const auth = this.auth;
      const user = auth.currentUser;
      if (user) {
        this.user = user;
      } else {
        this.logout();
      }
    } catch (error: any) {
      this.logout();
      this.toastr.error(error.message);
    }
  }

  async logout() {
    const firebaseAuth = this.auth;
    await firebaseAuth.signOut();
    this.user = null;
    this.router.navigate(['/login']);
  }



  signInByMicrosoft() {
    return this.AuthLogin(new OAuthProvider('microsoft.com'));
  }

  /**
 * Authenticates a user using the specified authentication provider and blocks logins from public email domains.
 *
 * @param provider The authentication provider (e.g., GoogleAuthProvider).
 * @returns A promise that resolves to the navigation result if login is successful and email domain is not from a public provider, or rejects with an error.
 */
  AuthLogin(provider: OAuthProvider): Promise<any> {
    return this.authServiceAuthLogin(provider).then((res: any) => {
      if (res) {
        // Check for a redirect URL in localStorage
        const redirectUrl = sessionStorage.getItem('redirectUrl');
        this.user = res.user;
        // Navigate to the redirect URL if it exists, otherwise go to the default page
        return this.router.navigate([redirectUrl || '/dashboard']).then(() => {
          // Clear the redirect URL from localStorage after navigation
          if (redirectUrl) {
            sessionStorage.removeItem('redirectUrl');
          }
        }).catch(navError => {
          console.error('Navigation Error:', navError);
          return Promise.reject(navError);
        });
      } else {
        console.error('Authentication Failed:', res);
        return Promise.reject(res);
      }
    }).catch((error: any) => {
      console.error('Login Error:', error);
      return Promise.reject(error);
    });
  }



  /**
   * Runs authentication providers (Google, Microsoft, NOT EMAIL/PASS) for login.
   * @param provider The authentication provider.
   * @returns A promise that resolves to an object with a success property indicating the success of the login.
   */
  async authServiceAuthLogin(provider: AuthProvider) {
    try {
      const res: UserCredential = await signInWithPopup(getAuth(), provider);
      if (res.user?.email) {
        const emailDomain = res.user.email.split('@')[1].toLowerCase();

        if (!allowedDomains.includes(emailDomain)) {
          console.error('Login using public email providers is not allowed.');
          await this.logout();
          return { success: false, error: 'Login using public email providers is not allowed.' };
        }
      }

      // const success = this.signinUser(res);
      this.user = res.user;
      return res;
    } catch (error: any) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.error('Account exists with different credential:', error.customData);
        // await this.handleAccountExists(error.customData);
      }
      console.error(error);
      return { success: false, error };
    }
  }

  /**
   * Recovers a user's password from firebase.
   * @param value
   */
  recoverPassword(value: any) {
    return this.authServiceRecoverPassword(value).then((res: any) => {
      if (res) {
        return res;
      } else {
        console.error('Password Recovery Failed:', res);
        return Promise.reject(res);
      }
    }).catch((error: any) => {
      console.error('Password Recovery Error:', error);
      return Promise.reject(error);
    });
  }

  authServiceRecoverPassword(value: any) {
    return this.auth.sendPasswordResetEmail(value.email);
  }
}
